import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { useTenant } from '../../contexts/TenantContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ShieldCheck, Database, Server, Users, Activity, Settings, HardDrive, RefreshCw, AlertTriangle, MessageSquare, Monitor, CreditCard, DollarSign, ChevronLeft, ChevronRight, Search, Trash2, Download, Layers, Newspaper } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAdminUsers, useAdminNodes, useAdminSync, useAdminHealth } from '../../hooks/useAdmin';
import { useLedgerTransactions } from '../../hooks/useLedger';
import { Spinner } from '../../components/ui/Spinner';
import { apiWorker } from '../../api/worker';
import { cn } from '../../lib/utils';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { AnnouncementComposer } from '../../components/pulse/AnnouncementComposer';


export const AdminUsersManager = () => {
  const { isSuperAdmin, isSupportAdmin, isOperationsAdmin } = usePermissions();
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isLoading } = useAdminUsers(page, 12);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  
  // Custom states for Support & Relationship features
  const [showRosterForm, setShowRosterForm] = useState(false);
  const [rosterNodeId, setRosterNodeId] = useState('');
  const [rosterRole, setRosterRole] = useState('MEMBER');
  
  const [showGuardianFixer, setShowGuardianFixer] = useState(false);
  const [dependentId, setDependentId] = useState('');
  
  const queryClient = useQueryClient();

  const roleMutation = useMutation({
    mutationFn: async (role: string) => {
      await apiWorker.patch(`/api/admin/users/${selectedUser.id}/platform-role`, { role });
    },
    onSuccess: () => {
      toast.success('Platform role updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: () => toast.error('Failed to update platform role')
  });

  const impersonateMutation = useMutation({
    mutationFn: async () => {
      await apiWorker.post(`/api/admin/users/${selectedUser.id}/impersonate`);
    },
    onSuccess: () => toast.success('Impersonating user session...'),
    onError: () => toast.error('Failed to impersonate')
  });

  const mfaResetMutation = useMutation({
    mutationFn: async () => {
      await apiWorker.delete(`/api/admin/users/${selectedUser.id}/auth/mfa`);
    },
    onSuccess: () => toast.success('Multi-Factor Authentication cleared'),
    onError: () => toast.error('Failed to clear MFA')
  });

  const verifyEmailMutation = useMutation({
    mutationFn: async () => {
      await apiWorker.post(`/api/admin/users/${selectedUser.id}/auth/verify-email`);
    },
    onSuccess: () => toast.success('Email manually verified'),
    onError: () => toast.error('Failed to manually verify email')
  });

  const statusMutation = useMutation({
    mutationFn: async (active: boolean) => {
      await apiWorker.patch(`/api/admin/users/${selectedUser.id}/status`, { active });
    },
    onSuccess: (_, active) => {
      toast.success(active ? 'User Activated' : 'User Disabled');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: () => toast.error('Failed to update account status')
  });

  const purgeMutation = useMutation({
    mutationFn: async () => {
      await apiWorker.post(`/api/admin/platform/purge`, { userId: selectedUser.id });
    },
    onSuccess: () => {
      toast.success('GDPR Data Purged');
      setSelectedUser(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: () => toast.error('Failed to purge user data')
  });

  const exportProfileMutation = useMutation({
    mutationFn: async () => {
      await apiWorker.post(`/api/admin/platform/export`, { userId: selectedUser.id });
    },
    onSuccess: () => toast.success('Profile data export triggered'),
    onError: () => toast.error('Failed to export profile data')
  });

  const manualPlacementMutation = useMutation({
    mutationFn: async ({ nodeId, role }: { nodeId: string; role: string }) => {
      await apiWorker.post(`/api/admin/nodes/${nodeId}/members/${selectedUser.id}/manual-placement`, { role });
    },
    onSuccess: () => {
      toast.success('Roster placement added successfully');
      setShowRosterForm(false);
      setRosterNodeId('');
    },
    onError: () => toast.error('Failed to place user on roster')
  });

  const linkDependentMutation = useMutation({
    mutationFn: async (depId: string) => {
      await apiWorker.post(`/api/admin/users/${selectedUser.id}/dependents/${depId}`);
    },
    onSuccess: () => {
      toast.success('Dependent account linked successfully');
      setDependentId('');
    },
    onError: () => toast.error('Failed to link dependent')
  });

  const unlinkDependentMutation = useMutation({
    mutationFn: async (depId: string) => {
      await apiWorker.delete(`/api/admin/users/${selectedUser.id}/dependents/${depId}`);
    },
    onSuccess: () => {
      toast.success('Dependent account unlinked successfully');
      setDependentId('');
    },
    onError: () => toast.error('Failed to unlink dependent')
  });

  const muteMutation = useMutation({
    mutationFn: async (hours: number) => {
      await apiWorker.post(`/api/admin/moderation/users/${selectedUser.id}/mute`, { durationMinutes: hours * 60 });
    },
    onSuccess: (_, hours) => {
      toast.success(`User muted globally for ${hours === 999999 ? 'permanent' : hours + 'h'}`);
    },
    onError: () => toast.error('Failed to mute user')
  });

  // Query events timeline for selected user
  const { data: userEvents = [], isLoading: isLoadingEvents } = useQuery({
    queryKey: ['admin', 'users', selectedUser?.id, 'events'],
    queryFn: async () => {
      try {
        const res = await apiWorker.get(`/api/admin/telemetry/users/${selectedUser.id}/events`);
        if (Array.isArray(res.data)) return res.data;
        if (Array.isArray(res.data?.content)) return res.data.content;
        if (Array.isArray(res.data?.events)) return res.data.events;
        return [];
      } catch {
        return [];
      }
    },
    enabled: !!selectedUser
  });

  if (selectedUser) {
    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-none border-2 border-black dark:border-white dark:border-white/80 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <div className="flex items-center gap-4">
            <Button variant="secondary" size="sm" onClick={() => setSelectedUser(null)} className="gap-2">
              <ChevronLeft size={16} /> Back to Users
            </Button>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {selectedUser.fullName || selectedUser.email}
                <Badge variant={selectedUser.role === 'SUPER_ADMIN' ? 'red' : 'blue'} className="text-xs">{selectedUser.role}</Badge>
              </h2>
              <p className="text-xs text-gray-500 font-mono mt-1">ID: {selectedUser.id} • Email: {selectedUser.email}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6">
            <div className="border-b dark:border-white pb-4">
              <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
                Support Actions
              </h3>
              <p className="text-xs text-gray-500 mt-1">Perform administrative tasks for this user account.</p>
            </div>
            
            <div className="space-y-3">
              {isSuperAdmin && (
                <Button 
                  variant="secondary" 
                  className="w-full text-xs font-semibold py-3 justify-center gap-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-200 transition-all rounded-none" 
                  onClick={() => impersonateMutation.mutate()} 
                  disabled={impersonateMutation.isPending}
                >
                  Impersonate Session
                </Button>
              )}
              
              {!isOperationsAdmin && (
                <div className="flex gap-2">
                  <Button 
                    variant="secondary" 
                    className="flex-1 text-xs justify-center py-2.5 rounded-none" 
                    onClick={() => mfaResetMutation.mutate()}
                    disabled={mfaResetMutation.isPending}
                  >
                    Reset MFA
                  </Button>
                  <Button 
                    variant="secondary" 
                    className="flex-1 text-xs justify-center py-2.5 rounded-none" 
                    onClick={() => verifyEmailMutation.mutate()}
                    disabled={verifyEmailMutation.isPending}
                  >
                    Verify Email
                  </Button>
                </div>
              )}
              
              {/* Manual Roster Placement form */}
              <div className="border-t dark:border-white pt-4">
                {showRosterForm ? (
                  <div className="space-y-3 bg-white dark:bg-slate-950 p-4 rounded-none border-2 border-black dark:border-white dark:border-white">
                    <h5 className="font-bold text-xs text-gray-700 dark:text-gray-300">Roster Placement</h5>
                    <input
                      type="text"
                      placeholder="Org ID or Slug"
                      className="w-full text-xs border-2 border-black dark:border-white rounded-none p-3 bg-white dark:bg-slate-900 border-black dark:border-white dark:border-white focus:ring-2 focus:ring-blue-500 outline-none"
                      value={rosterNodeId}
                      onChange={e => setRosterNodeId(e.target.value)}
                    />
                    <select
                      className="w-full text-xs border-2 border-black dark:border-white rounded-none p-3 bg-white dark:bg-slate-900 border-black dark:border-white dark:border-white focus:ring-2 focus:ring-blue-500 outline-none"
                      value={rosterRole}
                      onChange={e => setRosterRole(e.target.value)}
                    >
                      <option value="MEMBER">Member</option>
                      <option value="ADMIN">Admin</option>
                      <option value="LEADER">Leader</option>
                    </select>
                    <div className="flex gap-2 justify-end pt-1">
                      <Button size="sm" variant="secondary" onClick={() => setShowRosterForm(false)}>Cancel</Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {
                          if (!rosterNodeId) return toast.error('Org ID or Slug is required');
                          manualPlacementMutation.mutate({ nodeId: rosterNodeId, role: rosterRole });
                        }}
                        disabled={manualPlacementMutation.isPending}
                      >
                        Place User
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="secondary" className="w-full justify-center text-xs font-semibold py-3 rounded-none" onClick={() => setShowRosterForm(true)}>
                    + Manual Roster Placement
                  </Button>
                )}
              </div>

              {/* Guardian Dependent Fixer form */}
              {!isOperationsAdmin && (
                <div className="border-t dark:border-white pt-4">
                  {showGuardianFixer ? (
                    <div className="space-y-3 bg-white dark:bg-slate-950 p-4 rounded-none border-2 border-black dark:border-white dark:border-white">
                      <h5 className="font-bold text-xs text-gray-700 dark:text-gray-300">Guardian-Dependent Link Fixer</h5>
                      <input
                        type="text"
                        placeholder="Dependent Account User ID"
                        className="w-full text-xs border-2 border-black dark:border-white rounded-none p-3 bg-white dark:bg-slate-900 border-black dark:border-white dark:border-white focus:ring-2 focus:ring-blue-500 outline-none"
                        value={dependentId}
                        onChange={e => setDependentId(e.target.value)}
                      />
                      <div className="flex gap-2 justify-end pt-1">
                        <Button size="sm" variant="secondary" onClick={() => setShowGuardianFixer(false)}>Cancel</Button>
                        <Button
                          size="sm"
                          className="text-red-600 bg-red-50 hover:bg-red-100/80 border-red-100 rounded-none"
                          onClick={() => {
                            if (!dependentId) return toast.error('Dependent ID required');
                            unlinkDependentMutation.mutate(dependentId);
                          }}
                          disabled={unlinkDependentMutation.isPending}
                        >
                          Unlink
                        </Button>
                        <Button
                          size="sm"
                          className="text-green-600 bg-green-50 hover:bg-green-100/80 border-green-200 rounded-none"
                          onClick={() => {
                            if (!dependentId) return toast.error('Dependent ID required');
                            linkDependentMutation.mutate(dependentId);
                          }}
                          disabled={linkDependentMutation.isPending}
                        >
                          Link
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button variant="secondary" className="w-full justify-center text-xs font-semibold py-3 rounded-none" onClick={() => setShowGuardianFixer(true)}>
                      ⚙️ Guardian-Dependent Fixer
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6">
            <div className="border-b dark:border-white pb-4">
              <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
                Moderation & Safety
              </h3>
              <p className="text-xs text-gray-500 mt-1">Manage user's platform roles, access levels, and security states.</p>
            </div>
            <div className="space-y-3">
              {!isOperationsAdmin && (
                <div className="flex gap-2 items-center bg-orange-50/20 dark:bg-orange-950/10 p-3 rounded-none border-2 border-black dark:border-white border-orange-100/50 dark:border-orange-900/30">
                  <select 
                    id="mute-duration-select" 
                    className="border-2 border-black dark:border-white rounded-none text-xs p-2.5 flex-1 bg-white dark:bg-slate-900 border-black dark:border-white dark:border-white outline-none"
                  >
                    <option value="24">24 Hours</option>
                    <option value="168">7 Days</option>
                    <option value="999999">Permanent</option>
                  </select>
                  <Button 
                    variant="secondary" 
                    className="text-xs text-orange-600 border-orange-200 bg-white hover:bg-orange-50 dark:bg-slate-900 rounded-none"
                    onClick={() => {
                      const sel = document.getElementById('mute-duration-select') as HTMLSelectElement;
                      muteMutation.mutate(parseInt(sel.value));
                    }}
                    disabled={muteMutation.isPending}
                  >
                    Mute
                  </Button>
                </div>
              )}
              
              {isSuperAdmin && (
                <div className="space-y-3 border-t dark:border-white pt-3">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Set Platform Role</label>
                    <select 
                      className="w-full border-2 border-black dark:border-white rounded-none p-3 text-xs bg-white dark:bg-slate-900 border-black dark:border-white dark:border-white outline-none focus:ring-2 focus:ring-blue-500" 
                      defaultValue={selectedUser.role || "USER"} 
                      onChange={(e) => roleMutation.mutate(e.target.value)} 
                      disabled={roleMutation.isPending}
                    >
                      <option value="USER">Base User</option>
                      <option value="SUPPORT_ADMIN">Support Admin</option>
                      <option value="OPERATIONS_ADMIN">Operations Admin</option>
                      <option value="SUPER_ADMIN">Super Admin</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border-2 border-black dark:border-white rounded-none bg-white/50 dark:bg-slate-950 border-black dark:border-white dark:border-white">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Account Active</span>
                    <input 
                      type="checkbox" 
                      className="toggle" 
                      defaultChecked={selectedUser.active !== false} 
                      onChange={(e) => statusMutation.mutate(e.target.checked)} 
                      disabled={statusMutation.isPending}
                    />
                  </div>
                  
                  <div className="pt-4 border-t dark:border-white space-y-2">
                    <h4 className="text-xs font-bold text-red-500 uppercase tracking-wider">Danger Zone</h4>
                    <Button 
                      variant="secondary" 
                      className="w-full justify-center text-xs text-red-600 bg-red-50 hover:bg-red-100/80 border-red-100 rounded-none" 
                      onClick={() => {
                        if(window.confirm('Trigger GDPR Data Purge? This will permanently delete user records.')) {
                          purgeMutation.mutate();
                        }
                      }}
                      disabled={purgeMutation.isPending}
                    >
                      GDPR Data Purge
                    </Button>
                  </div>
                </div>
              )}
              
              {(isSuperAdmin || isSupportAdmin) && (
                <Button 
                  variant="secondary" 
                  className="w-full justify-center text-xs font-semibold py-3 border-t dark:border-white pt-4 rounded-none" 
                  onClick={() => exportProfileMutation.mutate()}
                  disabled={exportProfileMutation.isPending}
                >
                  📥 Export Profile Data
                </Button>
              )}
            </div>
          </Card>
        </div>

        <Card className="p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <h4 className="font-bold text-xs mb-3 text-gray-400 uppercase tracking-wider">Chronological Event Timeline</h4>
          {isLoadingEvents ? (
            <div className="flex justify-center p-4"><Spinner size="sm" /></div>
          ) : (
            <div className="space-y-3 h-40 overflow-y-auto text-xs pr-1 divide-y divide-gray-100 dark:divide-gray-800">
              {Array.isArray(userEvents) && userEvents.map((ev: any, idx: number) => (
                <div key={idx} className="flex gap-4 pt-2.5 first:pt-0">
                  <span className="text-gray-400 font-mono flex-shrink-0 text-[10px] w-28">
                    {new Date(ev.timestamp).toLocaleString()}
                  </span> 
                  <div className="flex-1">
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{ev.action}</span>
                    <span className="text-gray-500 ml-1">({ev.target})</span>
                    {ev.ipAddress && <span className="text-[10px] font-mono text-gray-400 block mt-0.5">IP: {ev.ipAddress}</span>}
                  </div>
                </div>
              ))}
              {(!Array.isArray(userEvents) || userEvents.length === 0) && (
                <p className="text-center text-gray-400 py-8">No events logged for this user.</p>
              )}
            </div>
          )}
        </Card>
      </div>
    );
  }

  const userList = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : []);
  const filteredUsers = userList.filter((u: any) => 
    u.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    u.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users size={20} className="text-purple-500" /> Platform Users
          </h2>
          <p className="text-xs text-gray-500">Overview and safety controls for all registered users.</p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-72">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search by name, email or ID..." 
              className="w-full pl-9 pr-4 py-2 text-xs border-2 border-black dark:border-white rounded-none bg-white dark:bg-slate-900 border-black dark:border-white dark:border-white focus:ring-2 focus:ring-blue-500 outline-none transition"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : (
        <div className="space-y-6">
          {filteredUsers.length === 0 ? (
            <Card className="flex flex-col items-center justify-center p-12 text-center border-2 border-black bg-white dark:bg-slate-900 rounded-none">
              <Users size={36} className="text-gray-300 dark:text-slate-700 mb-3" />
              <h3 className="font-semibold text-gray-700 dark:text-gray-300">No users found</h3>
              <p className="text-xs text-gray-400 max-w-xs mt-1">Could not find any users matching your search.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredUsers.map((u: any) => (
                <div 
                  key={u.id} 
                  className="p-5 bg-white dark:bg-slate-900 border-2 border-black dark:border-white dark:border-white hover:border-blue-500/50 dark:hover:border-blue-500/40 rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] cursor-pointer transition-all duration-200 flex flex-col justify-between"
                  onClick={() => setSelectedUser(u)}
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-blue-500 transition line-clamp-1">{u.fullName || u.email}</h4>
                      <Badge variant={u.role === 'SUPER_ADMIN' ? 'red' : 'blue'} className="text-[9px] uppercase tracking-wider">
                        {u.role}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-1">Email: {u.email}</p>
                    <p className="text-[10px] text-gray-400 font-mono">ID: {u.id}</p>
                  </div>

                  <div className="flex items-center justify-between border-t dark:border-white pt-3 mt-4">
                    <div className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline">
                      Manage User Settings →
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between border-t dark:border-white pt-4 mt-6">
              <span className="text-xs text-gray-500">
                Page <span className="font-semibold text-gray-700 dark:text-gray-300">{page + 1}</span> of <span className="font-semibold text-gray-700 dark:text-gray-300">{data.totalPages}</span>
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  disabled={page === 0} 
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  className="gap-1.5 px-3"
                >
                  <ChevronLeft size={14} /> Prev
                </Button>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  disabled={page >= data.totalPages - 1} 
                  onClick={() => setPage(p => p + 1)}
                  className="gap-1.5 px-3"
                >
                  Next <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
